/**
 * Top-Up Modal — two-step flow:
 * 1. Select amount (preset buttons or custom input)
 * 2. Pay with Stripe via PaymentModal
 */

import { useState, useRef, useEffect } from "react";
import { useBillingStore } from "@/stores/billingStore";
import { PaymentModal } from "./PaymentModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircle, Loader2, Check } from "lucide-react";
import { cn } from "@/lib/utils";

const PRESET_AMOUNTS = [1000, 2500, 5000, 10000, 50000]; // cents

interface TopUpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wsId: number;
}

export function TopUpModal({ open, onOpenChange, wsId }: TopUpModalProps) {
  const purchaseCredits = useBillingStore((s) => s.purchaseCredits);
  const fetchCredits = useBillingStore((s) => s.fetchCredits);
  const [step, setStep] = useState<"amount" | "payment" | "success">("amount");
  const [selectedCents, setSelectedCents] = useState<number | null>(null);
  const [customDollars, setCustomDollars] = useState("");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRefs = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    return () => {
      timerRefs.current.forEach(clearTimeout);
    };
  }, []);

  const reset = () => {
    setStep("amount");
    setSelectedCents(null);
    setCustomDollars("");
    setClientSecret(null);
    setLoading(false);
    setError(null);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      timerRefs.current.forEach(clearTimeout);
      timerRefs.current = [];
      reset();
    }
    onOpenChange(open);
  };

  const effectiveCents = selectedCents ?? (customDollars ? Math.round(parseFloat(customDollars) * 100) : 0);
  const isValidAmount = effectiveCents >= 1000; // min $10

  const handleContinue = async () => {
    if (!isValidAmount) return;

    setLoading(true);
    setError(null);

    const result = await purchaseCredits(wsId, effectiveCents);
    if (result?.clientSecret) {
      setClientSecret(result.clientSecret);
      setStep("payment");
    } else {
      setError("Failed to create payment. Please try again.");
    }
    setLoading(false);
  };

  const handlePaymentSuccess = async () => {
    setStep("success");
    // Poll for updated credit balance — webhook may take a few seconds
    let attempts = 0;
    const poll = async () => {
      try {
        await fetchCredits(wsId);
      } catch {
        // continue polling on transient errors
      }
      attempts++;
      if (attempts < 5) {
        timerRefs.current.push(setTimeout(poll, 2000));
      }
    };
    poll();
    // Auto-close after delay (balance may still update in background)
    timerRefs.current.push(setTimeout(() => handleClose(false), 4000));
  };

  if (step === "payment" && clientSecret) {
    return (
      <PaymentModal
        open={open}
        onOpenChange={handleClose}
        clientSecret={clientSecret}
        amountCents={effectiveCents}
        onSuccess={handlePaymentSuccess}
      />
    );
  }

  if (step === "success") {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Check className="w-6 h-6 text-emerald-500" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-lg">Payment successful</h3>
              <p className="text-sm text-muted-foreground mt-1">
                ${(effectiveCents / 100).toFixed(2)} in credits will be added to your workspace shortly.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Top up credits</DialogTitle>
          <DialogDescription>Choose an amount to add to your credit balance</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preset amounts */}
          <div className="grid grid-cols-3 gap-2">
            {PRESET_AMOUNTS.map((cents) => (
              <Button
                key={cents}
                variant={selectedCents === cents ? "default" : "outline"}
                size="sm"
                className={cn(
                  "h-10 tabular-nums",
                  selectedCents === cents && "ring-2 ring-primary ring-offset-2 ring-offset-background",
                )}
                onClick={() => {
                  setSelectedCents(cents);
                  setCustomDollars("");
                }}
              >
                ${(cents / 100).toFixed(0)}
              </Button>
            ))}
          </div>

          {/* Custom amount */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Custom:</span>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                type="number"
                min="10"
                step="1"
                placeholder="10.00"
                value={customDollars}
                onChange={(e) => {
                  setCustomDollars(e.target.value);
                  setSelectedCents(null);
                }}
                className="pl-7 h-9 tabular-nums"
              />
            </div>
          </div>

          {customDollars && !isValidAmount && (
            <p className="text-xs text-destructive">Minimum top-up amount is $10.00</p>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <Button
            className="w-full"
            disabled={!isValidAmount || loading}
            onClick={handleContinue}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Preparing payment...
              </>
            ) : isValidAmount ? (
              `Continue — $${(effectiveCents / 100).toFixed(2)}`
            ) : (
              "Select an amount"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
